/** Storage settings: R2 connection info, custom R2/S3 configuration, uploaded files with upload/preview/delete */

import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Upload, Trash2, FileIcon, Image, Download, Cloud, Info, X } from 'lucide-react'
import { useUploads, useUploadFile, useDeleteUpload } from '../../hooks/use-uploads'
import { cn } from '../../lib/utils'
import { API_BASE } from '../../lib/api-client'
import { formatBytes } from '../../lib/format-bytes'
import { useT } from '../../i18n'
import { useAppStore } from '../../stores/app-store'
import { StorageUploadDialog, type DescribedFile } from '../storage/storage-upload-dialog'
import { StorageConfigCard } from './storage-config-card'
import { SettingsSectionHeader } from './settings-section-header'
import { buttonPrimaryClass, cardClass } from '../ui/styles'

const statLabelClass = 'text-xs text-fg-subtle'

export function StorageTab() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const { data } = useUploads()
  const uploadFile = useUploadFile()
  const deleteUpload = useDeleteUpload()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null)
  // Picked files wait here until each one has a description (the API rejects uploads without one)
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null)

  useEffect(() => {
    if (!preview) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreview(null)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [preview])

  const files = data?.files ?? []
  const totalSize = files.reduce((sum, f) => sum + f.sizeBytes, 0)

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) setPendingFiles([file])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const startUpload = async (items: DescribedFile[]) => {
    setPendingFiles(null)
    const results = await Promise.allSettled(items.map((item) => uploadFile.mutateAsync(item)))
    const stored = results.filter((r) => r.status === 'fulfilled').length
    if (stored > 0) toast.success(t('storage.upload.done', { count: stored }))
    if (stored < items.length) toast.error(t('storage.upload.failed'))
  }

  const handleDelete = async (id: string, filename: string) => {
    if (!window.confirm(t('settings.storage.deleteConfirm', { name: filename }))) return
    await deleteUpload.mutateAsync(id)
  }

  return (
    <div className="space-y-4">
      <SettingsSectionHeader
        title={t('settings.storage.title')}
        description={t('settings.storage.summary', { count: files.length, size: formatBytes(totalSize, locale) })}
        action={
          <>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadFile.isPending} className={buttonPrimaryClass}>
              <Upload className="h-4 w-4" aria-hidden="true" />
              {uploadFile.isPending ? t('settings.storage.uploading') : t('settings.storage.upload')}
            </button>
            <input ref={fileInputRef} type="file" onChange={handlePick} className="hidden" />
          </>
        }
      />

      <div className={cn(cardClass, 'space-y-3 p-4')}>
        <div className="flex items-center gap-2">
          <Cloud className="h-4 w-4 text-accent-text" aria-hidden="true" />
          <span className="text-sm font-semibold text-fg">Cloudflare R2</span>
          <span className="rounded-md bg-success-soft px-1.5 py-0.5 text-xs font-medium text-success">{t('settings.storage.connected')}</span>
        </div>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <dt className={statLabelClass}>{t('settings.storage.bucket')}</dt>
            <dd className="mt-0.5 font-mono text-sm text-fg-muted">agentwiki-files</dd>
          </div>
          <div>
            <dt className={statLabelClass}>{t('settings.storage.maxFileSize')}</dt>
            <dd className="mt-0.5 text-sm text-fg-muted">10 MB</dd>
          </div>
          <div>
            <dt className={statLabelClass}>{t('settings.storage.totalFiles')}</dt>
            <dd className="mt-0.5 text-sm text-fg-muted">{files.length}</dd>
          </div>
          <div>
            <dt className={statLabelClass}>{t('settings.storage.totalSize')}</dt>
            <dd className="mt-0.5 text-sm text-fg-muted">{formatBytes(totalSize, locale)}</dd>
          </div>
        </dl>
        <p className="flex items-start gap-1.5 text-xs text-fg-subtle">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('settings.storage.r2Hint')}
        </p>
      </div>

      <StorageConfigCard />

      {files.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border-strong py-12 text-fg-subtle">
          <Image className="h-8 w-8" aria-hidden="true" />
          <p className="text-sm">{t('settings.storage.empty')}</p>
          <button type="button" onClick={() => fileInputRef.current?.click()} className="cursor-pointer text-sm text-accent-text hover:underline">
            {t('settings.storage.uploadFirst')}
          </button>
        </div>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {files.map((file) => {
            const url = `${API_BASE}/api/files/${file.fileKey}`
            const image = file.contentType.startsWith('image/')
            return (
              <li key={file.id} className={cn(cardClass, 'overflow-hidden')}>
                {image ? (
                  <button
                    type="button"
                    onClick={() => setPreview({ url, name: file.filename })}
                    aria-label={t('settings.storage.previewNamed', { name: file.filename })}
                    className="block w-full cursor-zoom-in"
                  >
                    <img src={url} alt="" className="h-28 w-full object-cover" loading="lazy" />
                  </button>
                ) : (
                  <div className="flex h-28 items-center justify-center bg-surface-muted">
                    <FileIcon className="h-8 w-8 text-fg-subtle" aria-hidden="true" />
                  </div>
                )}
                <div className="flex items-start gap-1 p-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-fg" title={file.filename}>
                      {file.filename}
                    </p>
                    <p className="text-xs text-fg-subtle">{formatBytes(file.sizeBytes, locale)}</p>
                  </div>
                  <a
                    href={url}
                    download={file.filename}
                    aria-label={t('settings.storage.downloadNamed', { name: file.filename })}
                    className="rounded-md p-1.5 text-fg-subtle hover:bg-surface-hover hover:text-fg"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => void handleDelete(file.id, file.filename)}
                    aria-label={t('settings.storage.deleteNamed', { name: file.filename })}
                    className="cursor-pointer rounded-md p-1.5 text-danger hover:bg-danger-soft"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {pendingFiles && (
        <StorageUploadDialog
          files={pendingFiles}
          folderName={t('storage.root')}
          onClose={() => setPendingFiles(null)}
          onSubmit={(items) => void startUpload(items)}
        />
      )}

      {preview && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={preview.name}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreview(null)}
        >
          <button type="button" onClick={() => setPreview(null)} aria-label={t('common.close')} className="absolute right-4 top-4 cursor-pointer rounded-md p-2 text-white hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
          <img src={preview.url} alt={preview.name} className="max-h-[80vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  )
}
